import React from "react"
import { Link } from "react-router-dom"
import Footer from "../components/footer"
import Navbar from "../components/nav"

function JobynePolicy() {
  return (
    <>
      <header className="header-area">
        <div className="navbar-area">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <Navbar />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div id="home">
        <div className="container mt-125 mb-50">
          <div className="card-body shadow">
            <h1>Privacy Policy</h1>
            <h4 className="mt-3">Jobyne - US Job Search</h4>
            <p className="mt-2"><strong>Last Updated: September 17, 2026</strong></p>
            <p><strong>Version: 1.3</strong></p>
            <p className="mt-2"><strong>Developer:</strong> MWX Apps</p>
            <p><strong>App Name:</strong> Jobyne: US Job Search</p>
            <p><strong>Package Name:</strong> com.jobyne</p>
            <p><strong>Contact Email:</strong> web-developer@outlook.com</p>

            <hr className="my-4" />

            {/* Introduction */}
            <h4 className="mt-4" id="introduction">Introduction</h4>
            <p className="mt-3">
              Welcome to Jobyne ("we," "our," or "us"). We are committed to
              protecting your privacy and ensuring you have a positive experience
              when using our Jobyne: US Job Search mobile application (the "App").
              This Privacy Policy explains what information we collect, why we
              collect it, where it is stored, and how you can access or delete it.
            </p>
            <p className="mt-3">
              Jobyne is a job search application that helps you find job openings
              across the United States. You can browse and search job listings
              <strong> without an account</strong>. Creating an account is optional
              and only needed if you want your profile and saved jobs to sync across
              devices.
            </p>
            <p className="mt-3">
              <strong className="text-primary">
                Our Core Privacy Principle: We collect only what an account needs. Your search
                history and app settings stay on your device.
              </strong>
            </p>

            <hr className="my-4" />

            {/* Data We Collect */}
            <h4 className="mt-4" id="collected">Information We Collect</h4>

            <h5 className="mt-4">1. If You Create an Account (Optional)</h5>
            <p className="mt-2">
              Browsing and searching jobs do <strong>not</strong> require an account.
              If you choose to register, we store the following on our servers:
            </p>
            <ul className="mt-2">
              <li><strong>Email address</strong> — identifies your account and is used to sign in</li>
              <li><strong>First and last name</strong> — shown on your profile</li>
              <li><strong>Password</strong> — authenticates you. Stored only as a bcrypt hash (cost 12); the plaintext is never written to disk or logged</li>
              <li><strong>City / location</strong> — pre-fills the location filter and your profile</li>
              <li><strong>Profile picture URL</strong> — an optional avatar, if you provide one</li>
            </ul>

            <h5 className="mt-4">2. If You Sign In with Google or LinkedIn</h5>
            <p className="mt-2">
              You may sign in with Google or LinkedIn instead of a password. In that
              case we receive your <strong>email address, name and profile-picture
              URL</strong> from that provider, and your account has
              <strong> no password</strong> on our side. We never receive your
              provider password, and we never post anything to those services on
              your behalf.
            </p>

            <h5 className="mt-4">3. If You Save Jobs (Favorites)</h5>
            <p className="mt-2">
              Saving a job stores a <code>(userId, jobId)</code> pair on our servers
              so your saved jobs follow you across devices. If you are signed out,
              saved jobs stay on your device only.
            </p>

            <h5 className="mt-4">4. Password Reset</h5>
            <p className="mt-2">
              If you use the "Forgot password" flow, we send a single-use,
              time-limited reset token to your email address. This token is used
              only to verify the reset request and expires automatically.
            </p>

            <h5 className="mt-4">5. Information Stored Only on Your Device</h5>
            <p className="mt-2">The following never leave your phone:</p>
            <ul className="mt-2">
              <li><strong>Search history</strong> — the queries you have run, used to power "Your recent searches"</li>
              <li><strong>App settings</strong> — theme, default filters, page size, animation preference</li>
              <li><strong>Saved jobs (signed out)</strong> — favourites kept locally until you sign in</li>
              <li><strong>Session tokens</strong> — access and refresh tokens, held in Android Keystore-backed encrypted storage</li>
            </ul>

            <h5 className="mt-4">6. Advertising (Google AdMob)</h5>
            <p className="mt-2">
              Advertising is being introduced in an upcoming version of the App.
              Once it is enabled, Jobyne will remain free to use and will be
              supported by ads served through <strong>Google AdMob</strong>. To
              serve and measure those ads, the Google Mobile Ads SDK may collect
              and process:
            </p>
            <ul className="mt-2">
              <li><strong>Advertising ID (AAID)</strong> — the resettable Android advertising identifier, used for ad frequency capping and, where you consent, personalisation</li>
              <li><strong>Device and app information</strong> — device model, operating-system version, app version, language and coarse (IP-based) location</li>
              <li><strong>Ad interaction data</strong> — which ads were shown, viewed or tapped, used for reporting and fraud prevention</li>
            </ul>
            <p className="mt-2">
              Where the law requires it (for example in the EEA and the UK), we ask
              for your consent before personalised advertising through Google's User
              Messaging Platform, and you can change or withdraw that choice at any
              time from the app's settings. You can also reset or delete your
              Advertising ID in <em>Android Settings → Google → Ads</em>. Google's
              handling of this data is governed by{" "}
              <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">Google's Advertising Policies</a>.
            </p>
            <p className="mt-2">
              <strong>Note:</strong> We never share your email, name or password with
              advertising partners, and ads are not shown on your account or saved-jobs
              screens in a way that conditions those features on viewing them.
            </p>

            <hr className="my-4" />

            {/* What We Do NOT Collect */}
            <h4 className="mt-4" id="not-collected">Information We Do NOT Collect</h4>
            <p className="mt-3">
              We want to be completely transparent about what we do NOT do:
            </p>
            <ul className="mt-2" style={{ listStyle: "none", paddingLeft: "10px" }}>
              <li>❌ We do NOT use Firebase Analytics, Google Analytics, or any behavioural tracking SDK</li>
              <li>❌ We do NOT use crash-reporting services that collect personal identifiers</li>
              <li>❌ We do NOT read your GPS location — the city list is fetched from our API and you type the location yourself</li>
              <li>❌ We do NOT access your contacts, camera, microphone, files or phone state</li>
              <li>❌ We do NOT sell or share your personal data with third parties for marketing</li>
              <li>❌ We do NOT profile you or make automated decisions about you</li>
            </ul>

            <hr className="my-4" />

            {/* How the App Works */}
            <h4 className="mt-4" id="how-it-works">How the App Works</h4>

            <h5 className="mt-4">Public Job Data</h5>
            <p className="mt-2">
              Jobyne does <strong>not generate</strong> its own job listings. When
              you search, the App sends your keyword and filters (e.g.,
              "software engineer", "Austin, TX", "remote") over HTTPS to the Jobyne
              API, which returns public listings. No device ID or advertising ID is
              ever attached to these requests.
            </p>
            <ul className="mt-2">
              <li><strong>Data Sent (signed out):</strong> Search keyword and filters only — these requests are anonymous</li>
              <li><strong>Data Sent (signed in):</strong> The same keyword and filters, plus your session token so the API can mark which listings you have already saved. The token identifies your account; the request carries no device ID or advertising ID</li>
              <li><strong>API Response:</strong> Public job listings (title, company, location, salary, description) are displayed and cached locally</li>
            </ul>
            <p className="mt-2">
              <strong>Note:</strong> When you tap "Apply" on a job listing, you are
              directed to the employer's or job board's own website. Any information
              you provide there is governed by <strong>their</strong> privacy policy,
              not ours. Jobyne never applies on your behalf.
            </p>

            <h5 className="mt-4">Authentication</h5>
            <p className="mt-2">
              If you sign in, your email and password (or OAuth handshake) are sent
              to the Jobyne API over HTTPS. All API traffic uses HTTPS and cleartext
              traffic is disabled in the app.
            </p>

            <hr className="my-4" />

            {/* Permissions */}
            <h4 className="mt-4" id="permissions">Permissions We Request</h4>

            <h5 className="mt-4">Internet Permission (Required)</h5>
            <ul className="mt-2">
              <li><strong>Purpose:</strong> To fetch job listings and authenticate your account</li>
              <li><strong>Usage:</strong> HTTPS requests only</li>
            </ul>
            <p className="mt-2">
              <strong>INTERNET is the only permission required for the core job-search experience.</strong>
            </p>

            <h5 className="mt-4">Advertising Permissions (Added with AdMob)</h5>
            <p className="mt-2">
              Once the AdMob advertising feature is enabled, the app will also
              declare the following permissions, required by the Google Mobile Ads
              SDK:
            </p>
            <ul className="mt-2">
              <li><strong>com.google.android.gms.permission.AD_ID</strong> — lets Google provide the resettable Advertising ID used to serve and measure ads (Android 13 and above). You can reset or delete this ID in Android Settings → Google → Ads</li>
              <li><strong>ACCESS_NETWORK_STATE</strong> — lets the ads SDK check whether a network connection is available before requesting an ad</li>
            </ul>
            <p className="mt-2">
              The app still does <strong>not</strong> request GPS location, contacts,
              camera, microphone, storage or phone-state permissions.
            </p>

            <hr className="my-4" />

            {/* Third-Party Services */}
            <h4 className="mt-4" id="third-party">Third-Party Services</h4>

            <h5 className="mt-4">Google &amp; LinkedIn (Optional Sign-In)</h5>
            <ul className="mt-2">
              <li><strong>Data Received:</strong> Email, name, profile-picture URL</li>
              <li><strong>Privacy:</strong> Please refer to their own privacy policies for how they handle your sign-in</li>
              <li><strong>We do not control</strong> these providers</li>
            </ul>

            <h5 className="mt-4">Google AdMob (Advertising)</h5>
            <ul className="mt-2">
              <li><strong>Purpose:</strong> Serves the ads shown in the App</li>
              <li><strong>Data Processed:</strong> Advertising ID, device and app information, coarse location, ad interactions</li>
              <li><strong>Privacy:</strong> See <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">Google's Advertising Policies</a> and <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a></li>
              <li><strong>We do not control</strong> Google or the ads it serves</li>
            </ul>

            <h5 className="mt-4">Cloud Hosting &amp; Email Delivery</h5>
            <ul className="mt-2">
              <li>A cloud hosting provider runs the Jobyne API and database, so your account data is stored there at rest</li>
              <li>An SMTP email provider is used only to deliver account emails such as password-reset messages</li>
              <li>Cloudflare serves the Jobyne website and handles standard request metadata</li>
            </ul>

            <h5 className="mt-4">We Do NOT Use:</h5>
            <ul className="mt-2">
              <li>Google Analytics or Firebase Analytics</li>
              <li>Facebook SDK or social media tracking SDKs</li>
              <li>Advertising networks other than Google AdMob (see "Advertising" above)</li>
              <li>Crash reporting that collects personal identifiers</li>
              <li>Any third-party tracking services other than the Google Mobile Ads SDK</li>
            </ul>

            <h5 className="mt-4">Google Play Services</h5>
            <p className="mt-2">
              The App is distributed through Google Play Store, which has its own
              privacy policy. Please refer to{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>{" "}
              for information about how Google handles data related to app distribution.
            </p>

            <hr className="my-4" />

            {/* Data Security */}
            <h4 className="mt-4" id="security">Data Security</h4>
            <ul className="mt-2">
              <li>All API traffic uses HTTPS. Cleartext traffic is disabled in the app</li>
              <li>Passwords are hashed with bcrypt; they are never stored or logged in plain text</li>
              <li>Sessions use short-lived access tokens (30 minutes) plus rotating refresh tokens (30 days), stored in Android Keystore-backed encrypted storage</li>
              <li>Sign-in tokens are delivered to the app over a private <code>jobyne://</code> URL scheme that the server only honours for completed provider handshakes</li>
            </ul>

            <hr className="my-4" />

            {/* Data Retention & Deletion */}
            <h4 className="mt-4" id="local-data">Data Retention and Deletion</h4>
            <p className="mt-3">
              Your account data is kept until you delete it.
            </p>
            <ul className="mt-2">
              <li><strong>Delete your account in the app:</strong> Account → <em>Delete account</em>. This permanently removes your profile, email, password hash, saved jobs and stored tokens. If your account has a password, we ask for it first, because the action cannot be undone</li>
              <li><strong>Delete local data:</strong> clearing the app's storage in Android Settings → Apps → Jobyne → Storage → Clear Data, or uninstalling the app, removes search history, settings and cached responses</li>
              <li><strong>By email:</strong> you can also request deletion by writing to web-developer@outlook.com</li>
            </ul>

            <hr className="my-4" />

            {/* Children's Privacy */}
            <h4 className="mt-4" id="childrens-privacy">Children's Privacy</h4>
            <p className="mt-3">
              Jobyne is not directed at children under the age of 13, and we do not
              knowingly collect personal information from them. If you are a parent
              and believe your child has provided personal information to us, please
              contact us at web-developer@outlook.com and we will assist.
            </p>

            <hr className="my-4" />

            {/* Your Rights */}
            <h4 className="mt-4" id="your-rights">Your Rights</h4>
            <p className="mt-3">
              Depending on where you live, you may have the right to access, correct,
              export or erase your personal data, and to object to processing. The
              in-app account deletion flow covers erasure; for anything else, contact
              web-developer@outlook.com.
            </p>

            <hr className="my-4" />

            {/* International Users */}
            <h4 className="mt-4" id="international">International Users</h4>
            <p className="mt-3">
              Jobyne focuses on job listings in the United States but can be used
              anywhere in the world. If you create an account, your account data is
              stored on our servers and may be transferred to and processed in
              countries other than your own. Your search history, settings and
              signed-out favourites remain on your device.
            </p>

            <hr className="my-4" />

            {/* Changes */}
            <h4 className="mt-4" id="changes">Changes to This Privacy Policy</h4>
            <p className="mt-3">
              We may update our Privacy Policy from time to time. Thus, you are
              advised to review this page periodically for any changes. We will
              notify you of any changes by updating the "Last Updated" date. These
              changes are effective immediately after they are posted on this page.
            </p>

            <hr className="my-4" />

            {/* Contact */}
            <h4 className="mt-4" id="contact">Contact Us</h4>
            <p className="mt-3">
              If you have any questions or suggestions about this Privacy Policy, do
              not hesitate to contact us at: web-developer@outlook.com
            </p>
            <p className="mt-2"><strong>Developer:</strong> MWX Apps</p>
            <p><strong>App:</strong> Jobyne: US Job Search (com.jobyne)</p>
            <p><strong>Website:</strong> https://mwx-apps.web.app/jobyne</p>

            <hr className="my-4" />

            {/* Summary */}
            <h4 className="mt-4" id="summary">Summary</h4>
            <div className="table-responsive mt-3">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Collected?</th>
                    <th>Shared?</th>
                    <th>Stored where</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Email address</td>
                    <td>✅ Yes, if you register</td>
                    <td>❌ No</td>
                    <td>Our database</td>
                  </tr>
                  <tr>
                    <td>Name, city, picture URL</td>
                    <td>✅ Yes, if you provide them</td>
                    <td>❌ No</td>
                    <td>Our database</td>
                  </tr>
                  <tr>
                    <td>Password</td>
                    <td>✅ Yes — bcrypt hash only</td>
                    <td>❌ No</td>
                    <td>Our database</td>
                  </tr>
                  <tr>
                    <td>Saved jobs (favourites)</td>
                    <td>✅ Yes, if you save any</td>
                    <td>❌ No</td>
                    <td>Our database</td>
                  </tr>
                  <tr>
                    <td>Search history</td>
                    <td>❌ No</td>
                    <td>❌ No</td>
                    <td>Your device only</td>
                  </tr>
                  <tr>
                    <td>App settings</td>
                    <td>❌ No</td>
                    <td>❌ No</td>
                    <td>Your device only</td>
                  </tr>
                  <tr>
                    <td>Location (GPS)</td>
                    <td>❌ No</td>
                    <td>❌ No</td>
                    <td>—</td>
                  </tr>
                  <tr>
                    <td>Advertising ID (AAID)</td>
                    <td>✅ Yes — via Google AdMob</td>
                    <td>✅ Processed by Google</td>
                    <td>Google (AdMob)</td>
                  </tr>
                  <tr>
                    <td>Analytics / usage</td>
                    <td>❌ No</td>
                    <td>❌ No</td>
                    <td>—</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-4">
              <h5 className="text-primary">Jobyne - US Job Search</h5>
              <p className="mt-2"><em>This privacy policy is effective as of September 17, 2026.</em></p>
              <p className="mt-1"><em>Jobyne is an independent job search app and is not affiliated with any employer, job board or third-party job data provider.</em></p>
              <p className="mt-1">
                <Link to="/jobyne/terms-and-conditions" style={{ color: "#0898e7", fontWeight: "600" }}>
                  Terms &amp; Conditions
                </Link>
              </p>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  )
}

export default JobynePolicy
